import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import {
  Briefcase, BookOpen, Award, ClipboardList, ArrowUpRight, Clock, Calendar,
  AlertCircle, CheckCircle2, TrendingUp, MapPin,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const ABS = (u) => (u && u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u);

export default function StudentDashboard() {
  const { user } = useAuth();
  const interest = user?.interest || "both";
  const showIntern = interest === "internship" || interest === "both";
  const showTests = interest === "test_series" || interest === "both";

  const [apps, setApps] = useState([]);
  const [tests, setTests] = useState([]);
  const [attempts, setAttempts] = useState([]);

  useEffect(() => {
    if (showIntern) api.get("/student/internships/my-applications").then((r) => setApps(r.data || [])).catch(() => {});
    if (showTests) {
      api.get("/student/test-series").then((r) => setTests(r.data || [])).catch(() => {});
      api.get("/exam/my-attempts").then((r) => setAttempts(r.data || [])).catch(() => {});
    }
  }, [showIntern, showTests]);

  const certs = apps.filter((a) => a.certificate_url).length;
  const pendingApps = apps.filter((a) => a.status === "pending").length;
  const approvedApps = apps.filter((a) => a.status === "approved").length;

  // Next upcoming test
  const nextTest = useMemo(() => {
    const now = Date.now();
    return tests
      .filter((t) => t.exam_date && new Date(t.exam_date).getTime() > now && !t.is_attempted)
      .sort((a, b) => new Date(a.exam_date) - new Date(b.exam_date))[0];
  }, [tests]);

  const photoUrl = user?.photo_url ? ABS(user.photo_url) : "";
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 shadow-sm">
            {photoUrl ? (
              <img src={photoUrl} alt={user?.name} className="w-full h-full object-cover" />
            ) : (
              <span className="font-black text-xl text-slate-400">{initials}</span>
            )}
          </div>
          <div>
            <div className="text-[11px] text-slate-500 font-mono">{today}</div>
            <h1 className="heading text-2xl md:text-[28px] font-black leading-tight mt-0.5" data-testid="student-dashboard-greeting">
              Good to see you, {user?.name?.split(" ")[0]}.
            </h1>
            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 flex-wrap">
              {user?.college && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {user.college}</span>}
              {user?.stream && <><span className="text-slate-300">·</span><span>{user.stream}</span></>}
              {interest === "both" && <><span className="text-slate-300">·</span><span>Internship + Test Series</span></>}
              {interest === "internship" && <><span className="text-slate-300">·</span><span>Internship track</span></>}
              {interest === "test_series" && <><span className="text-slate-300">·</span><span>Test Series track</span></>}
            </div>
          </div>
        </div>
      </div>

      {/* Next exam callout (only shown if there's an upcoming test) */}
      {nextTest && (
        <div className="mt-6 border-l-4 border-[#F59E0B] bg-amber-50/60 px-5 py-4 flex items-center justify-between gap-4 flex-wrap" data-testid="next-exam-callout">
          <div className="flex items-center gap-3">
            <Clock size={18} className="text-[#D97706]" />
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#D97706]">Next Exam</div>
              <div className="font-bold text-sm text-slate-900 mt-0.5">{nextTest.title}</div>
              <div className="text-xs text-slate-600 mt-0.5">
                <Calendar size={11} className="inline mr-1 -mt-px" />
                {new Date(nextTest.exam_date).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} · {nextTest.duration_minutes} min
              </div>
            </div>
          </div>
          {nextTest.is_enrolled ? (
            <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700"><CheckCircle2 size={13} /> Enrolled</span>
          ) : (
            <Link to="/student/test-series" className="text-xs font-black text-[#D97706] hover:text-[#F59E0B] inline-flex items-center gap-1" data-testid="next-exam-enroll">
              Enroll now <ArrowUpRight size={12} />
            </Link>
          )}
        </div>
      )}

      {/* Stats */}
      <div className={`mt-6 grid gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200 ${showIntern && showTests ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2"}`}>
        {showIntern && (
          <>
            <Stat to="/student/applications" icon={ClipboardList} value={apps.length} label="Applications" hint={pendingApps ? `${pendingApps} pending` : approvedApps ? `${approvedApps} approved` : "none yet"} />
            <Stat to="/student/applications" icon={Award} value={certs} label="Certificates" hint={certs ? "downloadable" : "pending"} />
          </>
        )}
        {showTests && (
          <>
            <Stat to="/student/test-series" icon={BookOpen} value={tests.filter((t) => !t.is_attempted).length} label="Available Tests" hint={`${tests.length} total`} />
            <Stat to="/student/results" icon={TrendingUp} value={attempts.length} label="Exams Taken" hint={attempts.length ? `last: ${attempts[0]?.score ?? "—"}/${attempts[0]?.total ?? "—"}` : "start anytime"} />
          </>
        )}
      </div>

      {/* Two-column content */}
      <div className={`mt-8 grid gap-6 ${showIntern && showTests ? "lg:grid-cols-2" : "grid-cols-1"}`}>
        {showIntern && (
          <section className="rounded-xl border border-slate-200 bg-white">
            <header className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <Briefcase size={16} className="text-brand" />
                <h2 className="heading font-black text-base">Internship Applications</h2>
              </div>
              <Link to="/student/internships" className="text-xs font-bold text-brand hover:text-[#F59E0B] inline-flex items-center gap-1">
                Browse vacancies <ArrowUpRight size={11} />
              </Link>
            </header>
            <div>
              {apps.length === 0 && (
                <div className="px-5 py-10 text-center">
                  <Briefcase size={28} className="mx-auto text-slate-200" />
                  <div className="mt-3 text-sm text-slate-500">No applications yet.</div>
                  <Link to="/student/internships" className="mt-3 inline-block text-xs font-bold text-brand hover:underline">Browse open roles →</Link>
                </div>
              )}
              {apps.slice(0, 5).map((a, i) => (
                <div key={a.id} className={`px-5 py-3.5 flex items-center justify-between gap-3 ${i !== 0 ? "border-t border-slate-100" : ""}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-slate-900 truncate">{a.internship_title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>Applied {new Date(a.applied_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                      {a.attendance_url && (
                        <a href={ABS(a.attendance_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-indigo-600 font-bold hover:underline" data-testid={`dash-attend-${a.id}`}>
                          · Attendance ↓
                        </a>
                      )}
                      {a.certificate_url && (
                        <a href={ABS(a.certificate_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-brand font-bold hover:underline" data-testid={`dash-cert-${a.id}`}>
                          · Certificate ↓
                        </a>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
              ))}
              {apps.length > 5 && (
                <Link to="/student/applications" className="block px-5 py-3 border-t border-slate-100 text-xs font-bold text-brand hover:bg-slate-50 text-center">
                  View all {apps.length} applications →
                </Link>
              )}
            </div>
          </section>
        )}

        {showTests && (
          <section className="rounded-xl border border-slate-200 bg-white">
            <header className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <BookOpen size={16} className="text-[#F59E0B]" />
                <h2 className="heading font-black text-base">Test Series</h2>
              </div>
              <Link to="/student/test-series" className="text-xs font-bold text-[#F59E0B] hover:text-[#D97706] inline-flex items-center gap-1">
                View all <ArrowUpRight size={11} />
              </Link>
            </header>
            <div>
              {tests.length === 0 && (
                <div className="px-5 py-10 text-center">
                  <BookOpen size={28} className="mx-auto text-slate-200" />
                  <div className="mt-3 text-sm text-slate-500">No tests available yet.</div>
                  <div className="mt-1 text-xs text-slate-400">New test series are added by admin periodically.</div>
                </div>
              )}
              {tests.slice(0, 5).map((t, i) => (
                <div key={t.id} className={`px-5 py-3.5 flex items-center justify-between gap-3 ${i !== 0 ? "border-t border-slate-100" : ""}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-slate-900 truncate">{t.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      {t.exam_date && (
                        <span className="inline-flex items-center gap-0.5"><Calendar size={10} />
                          {new Date(t.exam_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                        </span>
                      )}
                      <span>{t.duration_minutes}m</span>
                      <span className={t.is_free ? "text-emerald-600 font-semibold" : "text-slate-600"}>
                        {t.is_free ? "Free" : `₹${t.price}`}
                      </span>
                    </div>
                  </div>
                  {t.is_attempted ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold uppercase tracking-wider">Attempted</span>
                  ) : t.is_enrolled ? (
                    <Link to={`/exam/${t.id}`} className="text-xs px-3 py-1.5 rounded-md bg-[#F59E0B] text-white font-bold hover:bg-[#D97706]">Start</Link>
                  ) : (
                    <Link to="/student/test-series" className="text-xs font-bold text-[#D97706] hover:text-[#F59E0B] inline-flex items-center gap-0.5">
                      Enroll <ArrowUpRight size={11} />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Quick-look: recent results */}
      {showTests && attempts.length > 0 && (
        <section className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="heading text-base font-black">Recent exam results</h2>
            <Link to="/student/results" className="text-xs font-bold text-brand hover:text-[#F59E0B]">See all →</Link>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
            {attempts.slice(0, 3).map((a) => (
              <ResultRow key={a.id} a={a} />
            ))}
          </div>
        </section>
      )}

      {/* Interest upsell — subtle, not pushy */}
      {interest !== "both" && (
        <div className="mt-10 pt-6 border-t border-slate-200 text-sm text-slate-500 flex items-center justify-between flex-wrap gap-2">
          <span>Want to explore {interest === "internship" ? "Test Series" : "Internships"} as well?</span>
          <Link to="/student/profile" className="font-bold text-brand hover:text-[#F59E0B]">Update your preferences →</Link>
        </div>
      )}
    </DashboardLayout>
  );
}

function Stat({ to, icon: Icon, value, label, hint }) {
  return (
    <Link to={to} className="group bg-white px-5 py-4 hover:bg-slate-50 transition" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <Icon size={16} className="text-slate-400 group-hover:text-brand transition" />
        <ArrowUpRight size={12} className="text-slate-300 group-hover:text-brand transition" />
      </div>
      <div className="heading text-[28px] font-black mt-3 tabular-nums leading-none">{value}</div>
      <div className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-500 mt-1.5">{label}</div>
      {hint && <div className="text-[11px] text-slate-400 mt-0.5 truncate">{hint}</div>}
    </Link>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { bg: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
    approved: { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
    rejected: { bg: "bg-red-50 text-red-700 border-red-200", icon: AlertCircle },
  };
  const cfg = map[status] || { bg: "bg-slate-50 text-slate-600 border-slate-200", icon: Clock };
  const I = cfg.icon;
  return (
    <span className={`text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider ${cfg.bg}`}>
      <I size={9} /> {status}
    </span>
  );
}

function ResultRow({ a }) {
  const percent = a.total ? Math.round(((a.score || 0) / a.total) * 100) : null;
  const published = a.is_result_published;
  return (
    <div className="px-5 py-3.5 flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm text-slate-900 truncate">{a.test_title}</div>
        <div className="text-xs text-slate-500 mt-0.5">
          {a.submitted_at ? new Date(a.submitted_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
        </div>
      </div>
      {published ? (
        <div className="text-right">
          <div className="heading font-black text-lg tabular-nums">{a.score}/{a.total}</div>
          <div className={`text-[10px] font-bold uppercase tracking-wider ${percent >= 60 ? "text-emerald-600" : percent >= 40 ? "text-amber-600" : "text-red-600"}`}>{percent}%</div>
        </div>
      ) : (
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold uppercase tracking-wider">Awaiting result</span>
      )}
    </div>
  );
}
